//library
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// pathnames
import pathnames from "routes/pathnames";

// services
import api from "services/api";

// store
import { useAppDispatch } from "store/store";
import { setLanguage } from "store/slices/language-slice";

// hooks
import useWindowSize from "hooks/use-window-size";
import useTemplateContext from "hooks/use-template-context";
import { useLanguagePath } from "hooks/use-language-path";

// common
import serveRequestErrors from "common/serve-request-errors";
import { switchLanguageInPath } from "common/language-helpers";

// dto
import ErrorResponseDto from "dto/services/error-response-dto";
import { ArticleDetailsDto, ArticleDto } from "dto/components/app-article-info/app-article-dto";

// components
import AppNavbar from "components/app-navbar";
import AppFooter from "components/app-footer";
import AppButton from "components/app-button";
import AppNewsCard from "components/app-news-card";
import AppArticleCard from "components/app-article-card";
import AppArticleInfo from "components/app-article-info";
import AppArticleDetails from "components/app-article-details";

const PageArticle = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const getPath = useLanguagePath();
	const location = useLocation();
	const params = useParams();
	const sizes = useWindowSize();
	const { id } = useParams();
	const [articleDetails, setArticleDetails] = useState<ArticleDetailsDto>();
	const [similarArticles, setSimilarArticles] = useState<ArticleDto[]>([]);
	const [headerClassName, setHeaderClassName] = useState("app-navbar__fixed");
	const queryParams = useRef({ search: "", page: 0, size: 9 });
	const isMobileView = useMemo(() => (sizes?.[0] <= 489 ? true : false), [sizes]);

	const { getFilteredResources } = useTemplateContext();
	const connectPage = useMemo(() => getFilteredResources("page.article"), [getFilteredResources]);
	const getPageData = useCallback((key: string) => connectPage?.find((value) => value.key === key)?.value?.toString() ?? "", [connectPage]);
	const articleSimilar = useMemo(() => getPageData("page.article.similar"), [getPageData]);
	const articleViewAll = useMemo(() => getPageData("page.article.viewAll"), [getPageData]);
	
	const onHandleGetSimilarArticles = useCallback(async (id: string, category: string) => {
		try {
			const params = queryParams.current;

			const payload = { size: 10, page: params.page };

			const response = await api.get.similarCategory.article(id, category, payload);

			setSimilarArticles(response.data.data.list.content.slice(0, 3));
		} catch (error) {
			serveRequestErrors(error as Error | ErrorResponseDto);
		}
	}, []);

	const onHandleGetArticleDetails = useCallback(async () => {
		if (!id) return;

		try {
			const response = await api.get.articleDetails.article(id);
			const data = response.data.data;

			setArticleDetails(data);
			onHandleGetSimilarArticles(id, data.category);
		} catch (error) {
			serveRequestErrors(error as Error | ErrorResponseDto);
		}
	}, [id, onHandleGetSimilarArticles]);

	const onHandleLangChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newLang = event.target.value;
		dispatch(setLanguage(newLang));

		const currentLang = params.lang || "en";
		const newPath = switchLanguageInPath(location.pathname, currentLang, newLang);

		navigate(newPath);
	};

	useEffect(() => {
		const isFixedHeader = () => {
			const scrolledTo = window.scrollY;

			if (scrolledTo >= 120) setHeaderClassName("app-navbar__fixed--active");
			else setHeaderClassName("app-navbar__fixed");
		};

		window.addEventListener("scroll", isFixedHeader);

		return () => window.removeEventListener("scroll", isFixedHeader);
	}, []);

	useEffect(() => {
		onHandleGetArticleDetails();
	}, [onHandleGetArticleDetails]);

	return (
		<div className="page-article">
			<div className="article">
				<AppNavbar className={headerClassName} onChange={onHandleLangChange} />

				{/*prettier-ignore*/}
				<header className="article__header">
					<div className="article__header-img" style={{ backgroundImage: `linear-gradient(180deg, rgba(20, 20, 20, 0), rgba(20, 20, 20, 0.88) 99.99%, rgba(20, 20, 20, 1)), url("${articleDetails?.thumbnail}")` }}></div>
					<div className="article__header-text">
						<p>{articleDetails?.title}</p>
					</div>
				</header>

				<div className="content">
					<div className="content__left">
						<AppArticleInfo publicationDate={articleDetails?.publicationDate} category={articleDetails?.category} tableOfContent={articleDetails?.tableOfContent || []} location={articleDetails?.location} />
					</div>

					<div className="content__right">
						<AppArticleDetails content={articleDetails?.content || []} />
					</div>
				</div>

				<div className="articles-section">
					<div className="articles-section__header">
						<p className="articles-section__title">{articleSimilar}</p>

						{!isMobileView && <AppButton label={articleViewAll} onClick={() => navigate(getPath(pathnames.PageAllArticles))} tertiary />}
					</div>

					<div className="articles-section__wrapper">
						{similarArticles.map((o, i) => (
							<AppArticleCard title={o.title} description={o.category} image={o.thumbnail} key={o.id} location={o.location} date={o.date} link={`${pathnames.PageArticle}/${o.id}`} />
						))}
					</div>

					{isMobileView && (
						<div className="articles-section__button-wrapper">
							<AppButton label={articleViewAll} onClick={() => navigate(getPath(pathnames.PageAllArticles))} tertiary />
						</div>
					)}
				</div>

				<AppNewsCard />
				<AppFooter />
			</div>
		</div>
	);
};

export default PageArticle;
